import {
  ChartLineUp,
  Gear,
  HouseSimple,
  LightbulbFilament,
  Notepad,
  SignOut,
} from 'phosphor-react';
import EmoneyIcon from '../../../assets/Icons/emoney-icon';
import QuickBookIcon from '../../../assets/Icons/QuickBookIcon';
import SignInIcon from '../../../assets/Icons/SignInIcon';
import BarChartSquare from '../../../assets/Icons/bar-chart-square';
import { SidebarItem } from '../../Sidebar/types';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { StrategyService } from '../../../Service/StrategyService';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { onUserLogout } from '../../../store/user/user-slice';
import FileAttachmentIcon from '../../../assets/Icons/file-attachment-icon';
import CheckSquare from '../../../assets/Icons/check-square';

export const useLayoutItems = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const appDispatch = useAppDispatch();
  const [strategies, setStrategies] = useState<
    { id: number; name: string; targetDate: Date }[]
  >([]);
  const authUser = useAppSelector(state => state.user.user);

  const onLogout = () => {
    localStorage.clear();
    navigate('/');
    appDispatch(onUserLogout());
  };

  const getStrategies = async () => {
    const { data } = await StrategyService.getStrategies();

    setStrategies(
      data.map(strategy => ({
        id: strategy.id,
        name: strategy.name,
        targetDate: strategy.targetDate,
      }))
    );
  };

  useEffect(() => {
    getStrategies();
  }, []);

  const sidebarItems = useMemo(() => {
    const items = [
      {
        title: 'Home',
        path: '/home',
        icon: <HouseSimple size={20} color={'#202A2D'} />,
      },
      {
        title: 'Strategies',
        path: '/strategies',
        icon: <ChartLineUp size={20} color={'#202A2D'} />,
        breadCrumbs: strategies
          ?.filter(item => item.targetDate)
          .map(item => ({
            id: item.id,
            title: item.name,
          })),
      },
      {
        title: 'Reports',
        icon: <BarChartSquare />,
        path: '/reports',
      },
      {
        title: 'Documents',
        icon: <FileAttachmentIcon />,
        path: '/documents',
      },
      {
        title: 'Tasks',
        icon: <CheckSquare />,
        path: '/tasks',
      },
      {
        title: 'Knowledge Base',
        icon: <LightbulbFilament size={20} color={'#202A2D'} />,
        onClick: () =>
          window.open(
            'https://www.k2businessgroup.com/video-library',
            '_blank'
          ),
      },
      {
        title: 'Notes',
        icon: <Notepad size={20} color={'#202A2D'} />,
        path: '/notes',
      },
      {
        title: 'eMoney',
        icon: <EmoneyIcon />,
        endIcon: <SignInIcon />,
        onClick: () =>
          window.open(
            'https://wealth.emaplan.com/ema/SignIn?ema%2fria%2fk2businessgroup',
            '_blank'
          ),
      },
      {
        title: 'QuickBooks',
        icon: <QuickBookIcon />,
        endIcon: <SignInIcon />,
        onClick: () =>
          window.open(
            'https://accounts.intuit.com/app/sign-in?app_group=QBO&asset_alias=Intuit.accounting.core.qbowebapp&iux_tests=49561%3A8%3A119104&state=%7B%22queryParams%22%3A%7B%22loadCustomerAssistanceAssets%22%3A%22us%22%7D%7D&app_environment=prod&loadCustomerAssistanceAssets=us',
            '_blank'
          ),
      },
    ];

    return items.map(item => {
      return {
        ...item,
        selected: !!item?.path && location.pathname.includes(item.path),
        onClick:
          item.onClick ||
          (() => !!item.path && !item.breadCrumbs && navigate(item.path)),
        breadCrumbs: item.breadCrumbs?.map(breadCrumb => ({
          ...breadCrumb,
          selected: location.pathname === `${item.path}/${breadCrumb.id}`,
          onClick: () => navigate(`${item.path}/${breadCrumb.id}`),
        })),
      };
    });
  }, [location.pathname, strategies]);

  const dropdownItems = useMemo(() => {
    const items: SidebarItem[] = [
      {
        title: 'Logout',
        icon: <SignOut size={20} color={'#202A2D'} />,
        onClick: onLogout,
      },
    ];

    if (authUser?.role !== 'admin') {
      items.push({
        title: 'Settings',
        icon: <Gear size={20} color={'#202A2D'} />,
        selected: location.pathname === '/settings',
        onClick: () => navigate('/settings'),
      });
    }

    return items;
  }, []);

  return { sidebarItems, dropdownItems };
};
